import React from "react";
import PropTypes from "prop-types";
import { Grid, Container, Typography } from "@mui/material";
import { isEmpty, range, isNil } from "ramda";
import clsx from "clsx";

import withAlgoliaTracking from "components/withAlgoliaTracking";
import Placeholder from "components/Placeholder";
import StraightUp from "components/StraightUpCards/components/StraightUp";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";
import { useTrackAccountEvents, useUsers } from "hooks";

import StarringYou from "./components/StarringYou";
import useStyles from "./useStyles";

export const placeholderItemsToRenderSmall = 12;
export const placeholderItemsToRenderBig = 8;

const StarringYouAlgolia = withAlgoliaTracking(StarringYou);
const StraightUpAlgolia = withAlgoliaTracking(StraightUp);

const StarringYouCards = ({
  items,
  loading,
  title,
  size,
  lazy,
  overflow,
  center,
  isGrid,
  showDetails,
  modulePosition,
}) => {
  const { trackSelectItemEvents } = useTrackAccountEvents();
  const { currentUser } = useUsers();
  const hasModulePosition = Boolean(modulePosition);
  const classes = useStyles();

  const isSmall = size === "small";
  const lg = isSmall ? 2 : 3;

  const renderTitle = () => {
    if (loading && !isNil(title))
      return (
        <Typography data-testid="starringyoucards-placeholder" variant="h2">
          <Placeholder variant="text" />
        </Typography>
      );
    if (title && !isEmpty(items))
      return (
        <Typography className={isGrid ? classes.title : classes.centerTitle} variant="h2">
          {title}
        </Typography>
      );
    return null;
  };

  const renderContentScroll = () => {
    if (loading)
      return range(0, 6).map((index) => (
        <Container className={classes.itemScrolleable} key={`StarringYouCards-placeholder-${index}`}>
          <StarringYouAlgolia loading />
        </Container>
      ));

    return (
      items &&
      !isEmpty(items) &&
      items.map((item, index) => (
        <Container className={classes.itemScrolleable} key={`starring-you-cards-${size}-${item.id}`}>
          <StarringYouAlgolia lazy={index < 8 ? false : lazy} item={item} overflow={overflow} />
        </Container>
      ))
    );
  };

  const renderContentGrid = () => {
    const count = isSmall ? placeholderItemsToRenderSmall : placeholderItemsToRenderBig;
    if (loading)
      return range(0, count).map((index) => (
        <Grid key={`StarringYouCards-placeholder-${index}`} item xs={6} sm={3} md={3} lg={lg}>
          <StarringYouAlgolia loading />
        </Grid>
      ));

    return (
      items &&
      !isEmpty(items) &&
      items.map((item, index) => {
        const isStraightUp = TemplateGroupPresenter.isStraightUp(item);
        const sharedProps = {
          lazy: index < 8 ? false : lazy,
          item,
          hasModulePosition,
          onClick: hasModulePosition
            ? () =>
                trackSelectItemEvents(currentUser, item, modulePosition ? `${modulePosition}:${index + 1}` : undefined)
            : undefined,
        };

        return (
          <Grid key={`starring-you-cards-${size}-${item.id}`} item xs={6} sm={3} md={3} lg={lg}>
            {isStraightUp ? (
              <StraightUpAlgolia {...sharedProps} />
            ) : (
              <StarringYouAlgolia {...sharedProps} overflow={overflow} showDetails={showDetails} />
            )}
          </Grid>
        );
      })
    );
  };

  return (
    <Container className={clsx(classes.root, { [classes.center]: !isGrid })}>
      {renderTitle()}
      {!isGrid ? (
        <Container className={classes.contentScroll}>{renderContentScroll()}</Container>
      ) : (
        <Grid
          container
          spacing={2}
          className={clsx(classes.grid, { [classes.centerGrid]: center })}
          data-testid="starringyoucards-container"
        >
          {renderContentGrid()}
        </Grid>
      )}
    </Container>
  );
};

StarringYouCards.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      attributes: PropTypes.shape({}),
    }),
  ),
  loading: PropTypes.bool,
  title: PropTypes.string,
  size: PropTypes.oneOf(["small", "big"]),
  lazy: PropTypes.bool,
  overflow: PropTypes.bool,
  center: PropTypes.bool,
  isGrid: PropTypes.bool,
  showDetails: PropTypes.bool,
  modulePosition: PropTypes.number,
};

StarringYouCards.defaultProps = {
  title: "Starring You Ecards",
  size: "big",
  loading: false,
  lazy: true,
  overflow: false,
  center: false,
  isGrid: true,
  showDetails: false,
};

export default StarringYouCards;
